/**
 * POS Cart Calculation Helpers — Phase 2A
 *
 * Centralized, reusable functions for cart line and total calculations.
 * Supports: normal items, weight-based items, quantity items, per-item tax groups.
 * Compatible with both browser POS and future desktop Electron POS.
 */

export interface POSCartItem {
  id: number;
  productName: string;
  productPrice: number;
  salePrice: number;
  offerPrice?: number;
  quantity: number;
  variantName?: string;
  variantPrice?: number;
  type?: "product" | "modifier" | "variant" | "serialized";
  serializedItems?: any[];
  productCategoryId?: number;
  productConversionUnit?: number[];
  slotIds?: number[];
  slotTimes?: string[];
  date?: string;
  wp_page_id?: string;
  // Phase 2A fields
  is_weight_based?: boolean;
  tax_rate?: number;          // percentage (e.g. 13 for 13%)
  tax_name?: string;          // e.g. "HST"
  base_unit_id?: number;
  unit_abbreviation?: string; // e.g. "kg", "lb"
  pos_requires_quantity?: boolean;
}

export interface CartTotals {
  subTotal: number;
  taxTotal: number;
  grandTotal: number;
  itemCount: number;
}

// ─── Price resolution ────────────────────────────────────────────────────────

/** Get the effective selling price for a cart item (follows existing POS logic) */
export const getSellingPrice = (item: POSCartItem): number => {
  if (item.variantPrice && item.variantPrice > 0) return item.variantPrice;
  if (item.offerPrice && item.offerPrice > 0) return item.offerPrice;
  if (item.salePrice && item.salePrice > 0) return item.salePrice;
  return item.productPrice || 0;
};

// Alias for backward compat
export const getItemPrice = getSellingPrice;

// ─── Quantity resolution ─────────────────────────────────────────────────────

/** Get effective quantity — handles weight-based and legacy KG conversion */
export const getEffectiveQty = (item: POSCartItem): number => {
  // Weight-based: quantity IS the weight (e.g. 2.350)
  if (item.is_weight_based) return item.quantity;
  // Legacy: productConversionUnit includes 6 (KG) — stored in grams
  if (item.productConversionUnit?.includes(6)) return item.quantity / 1000;
  return item.quantity;
};

// ─── Line calculations ──────────────────────────────────────────────────────

/** Subtotal for a single cart line (price × qty, before tax) */
export const calculateLineSubtotal = (item: POSCartItem): number => {
  return getSellingPrice(item) * getEffectiveQty(item);
};

/** Tax for a single cart line */
export const calculateLineTax = (item: POSCartItem): number => {
  const rate = item.tax_rate ?? 0;
  return calculateLineSubtotal(item) * (rate / 100);
};

/** Total for a single cart line (subtotal + tax) */
export const calculateLineTotal = (item: POSCartItem): number => {
  return calculateLineSubtotal(item) + calculateLineTax(item);
};

// ─── Cart totals ─────────────────────────────────────────────────────────────

/** Calculate all cart totals across all items */
export const calculateCartTotals = (items: POSCartItem[]): CartTotals => {
  let subTotal = 0;
  let taxTotal = 0;

  for (const item of items) {
    subTotal += calculateLineSubtotal(item);
    taxTotal += calculateLineTax(item);
  }

  return {
    subTotal: parseFloat(subTotal.toFixed(2)),
    taxTotal: parseFloat(taxTotal.toFixed(2)),
    grandTotal: parseFloat((subTotal + taxTotal).toFixed(2)),
    itemCount: items.length,
  };
};

// ─── Display helpers ─────────────────────────────────────────────────────────

/** Format quantity for cart display. Weight items: "2.350 kg", normal: "3" */
export const formatQtyDisplay = (item: POSCartItem): string => {
  if (item.is_weight_based) {
    const unit = item.unit_abbreviation || "kg";
    return `${item.quantity.toFixed(3)} ${unit}`;
  }
  if (item.productConversionUnit?.includes(6)) {
    return `${(item.quantity / 1000).toFixed(3)} kg`;
  }
  if (item.productConversionUnit?.includes(5)) {
    return `${item.quantity} g`;
  }
  return String(item.quantity);
};

/** Format line detail for weight items: "2.350 kg × $3.50 = $8.23" */
export const formatLineDisplay = (item: POSCartItem): string => {
  if (item.is_weight_based) {
    const unit = item.unit_abbreviation || "kg";
    const price = getSellingPrice(item);
    const total = calculateLineSubtotal(item);
    return `${item.quantity.toFixed(3)} ${unit} × $${price.toFixed(2)} = $${total.toFixed(2)}`;
  }
  return "";
};
